import React from 'react';
import { useTranslate } from 'hooks/useTranslate';
import Button from '@mui/material/Button';
import DialogActions from '@mui/material/DialogActions';
import DialogTitle from '@mui/material/DialogTitle';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Rating from '@mui/material/Rating';
import { styled } from '@mui/system';
import HintIcon from 'assets/icons/hint.svg?react';
import { isAuthenticated } from 'helpers/authToken';
import NeedAuthDialog from 'components/NeedAuthDialog';

const TextFieldStyled = styled(TextField)(({ theme }) => ({
  backgroundColor: theme.palette.white.main
}));

const AddButton = styled(Button)(() => ({
  boxShadow: 'none',
  fontWeight: 500,
  fontSize: 16
}));

const DialogContentWrapper = styled(DialogContent)(() => ({
  padding: 24
}));

const FieldHeadline = styled(Typography)(({ theme }) => ({
  marginBottom: 10,
  fontSize: 16,
  fontWeight: 500,
  [theme.breakpoints.down('sm')]: {
    marginBottom: 0
  }
}));

const GridWrapper = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: 24,
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: '1fr'
  }
}));

const QuestionsCol = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 12
}));

const QuestionCard = styled('div')(() => ({
  backgroundColor: 'rgba(246, 244, 252, 1)',
  borderRadius: 16,
  padding: 16,
  border: '1px solid rgba(238, 237, 242, 1)'
}));

const QuestionCardFlex = styled(QuestionCard)(() => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between'
}));

const AlignAction = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'flex-end',
  marginTop: 20,
  [theme.breakpoints.down('sm')]: {
    justifyContent: 'center',
    alignItems: 'center'
  }
}));

const QuestionTitle = styled(Typography)(() => ({
  fontSize: 14,
  lineHeight: '20px',
  fontWeight: 500,
  marginBottom: 10
}));

const RequiredNote = styled(Typography)(({ theme }) => ({
  fontSize: 14,
  color: theme.palette.purple.main,
  marginLeft: 12,
  [theme.breakpoints.down('sm')]: {
    marginLeft: 0
  }
}));

const PurpleRating = styled(Rating)(({ theme }) => ({
  '& .MuiRating-iconFilled': {
    color: theme.palette.purple.main
  },
  '& .MuiRating-iconHover': {
    color: theme.palette.purple.main
  },
  '& .MuiRating-iconFocus': {
    color: theme.palette.purple.main
  }
}));

const RowSpaceBetween = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 8
  }
}));

const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: 8,
  boxShadow: 'none',
  backgroundColor: 'rgba(10, 10, 10, 1)',
  fontSize: 16,
  fontWeight: 500,
  padding: '10px 15px',
  color: theme.palette.white.main,
  '&:hover': {
    boxShadow: 'none',
    backgroundColor: 'rgba(10, 10, 10, 1)'
  },
  [theme.breakpoints.down('sm')]: {
    width: '100%'
  }
}));

const AddReviewAction = ({
  children = 'Додати відгук',
  buttonProps = {},
  onSubmit,
  mode = 'complex',
  ratingLabel
}) => {
  const [open, setOpen] = React.useState(false);
  const [needAuth, setNeedAuth] = React.useState(false);
  const [ratings, setRatings] = React.useState({ q1: 0, q2: 0, q3: 0, q4: 0, q5: 0 });
  const [message, setMessage] = React.useState('');
  const [errors, setErrors] = React.useState({
    q1: false,
    q2: false,
    q3: false,
    q4: false,
    q5: false,
    message: false
  });
  const [success, setSuccess] = React.useState(false);

  const t = useTranslate('ReviewModal');

  const handleOpen = () => {
    if (!isAuthenticated()) {
      setNeedAuth(true);
      return;
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setNeedAuth(false);
    setRatings({ q1: 0, q2: 0, q3: 0, q4: 0, q5: 0 });
    setMessage('');
    setErrors({ q1: false, q2: false, q3: false, q4: false, q5: false, message: false });
  };

  const handleSubmit = async () => {
    const questionKeys = mode === 'simple' ? ['q1'] : ['q1', 'q2', 'q3', 'q4', 'q5'];
    let hasError = false;
    const nextErrors = { q1: false, q2: false, q3: false, q4: false, q5: false, message: false };

    questionKeys.forEach((k) => {
      if (!ratings[k]) {
        nextErrors[k] = true;
        hasError = true;
      }
    });
    if (!message || !message.trim()) {
      nextErrors.message = true;
      hasError = true;
    }
    setErrors(nextErrors);
    if (hasError) return;

    if (onSubmit) {
      try {
        await onSubmit({
          rating: ratings.q1,
          message,
          ratings: questionKeys.reduce((acc, k) => ({ ...acc, [k]: ratings[k] }), {})
        });
      } catch (e) {}
    }
    setOpen(false);
    setSuccess(true);
  };

  const questions =
    mode === 'simple'
      ? [
          {
            key: 'q1',
            label: ratingLabel || 'Rating'
          }
        ]
      : [
          { key: 'q1', label: t('QTeachers') },
          { key: 'q2', label: t('QPracticalSkills') },
          { key: 'q3', label: t('QStudentsInteraction') },
          { key: 'q4', label: t('QScheduleConvenience') },
          { key: 'q5', label: t('QCostVsResult') }
        ];

  const questionsContent = (
    <QuestionsCol>
      {questions.map(({ key, label }) => (
        <QuestionCard key={key}>
          <QuestionTitle>{label}</QuestionTitle>
          <RowSpaceBetween>
            <PurpleRating
              value={ratings[key]}
              onChange={(_, v) => {
                const val = v || 0;
                setRatings((prev) => ({ ...prev, [key]: val }));
                if (errors[key] && val) setErrors((prev) => ({ ...prev, [key]: false }));
              }}
            />
            {errors[key] ? <RequiredNote>{t('RequiredQuestion')}</RequiredNote> : null}
          </RowSpaceBetween>
        </QuestionCard>
      ))}
    </QuestionsCol>
  );

  const messageContent = (
    <QuestionCardFlex key="message">
      <div>
        <RowSpaceBetween>
          <FieldHeadline>{t('TellMore')}</FieldHeadline>
          {errors.message ? <RequiredNote>{t('RequiredQuestion')}</RequiredNote> : null}
        </RowSpaceBetween>
        <TextFieldStyled
          placeholder={t('MessagePlaceholderDetailed') || t('MessagePlaceholder')}
          rows={6}
          fullWidth
          multiline
          value={message}
          onChange={(e) => {
            const v = e.target.value;
            setMessage(v);
            if (errors.message && v.trim()) setErrors((prev) => ({ ...prev, message: false }));
          }}
        />
      </div>
      <AlignAction>
        <ActionButton onClick={handleSubmit}>{t('SendFeedback') || t('Submit')}</ActionButton>
      </AlignAction>
    </QuestionCardFlex>
  );

  return (
    <>
      <AddButton startIcon={<HintIcon />} {...buttonProps} onClick={handleOpen}>
        {children}
      </AddButton>

      <NeedAuthDialog handleClose={() => setNeedAuth(false)} needAuth={needAuth} />

      <Dialog open={open} onClose={handleClose} scroll="body" fullWidth maxWidth="md">
        <DialogContentWrapper>
          {mode === 'simple' ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
              {questionsContent}
              {messageContent}
            </div>
          ) : (
            <GridWrapper>
              {questionsContent}
              {messageContent}
            </GridWrapper>
          )}
        </DialogContentWrapper>
      </Dialog>
      {success && (
        <Dialog open={success} onClose={() => setSuccess(false)} fullWidth maxWidth="sm">
          <DialogTitle>{t('ReviewSuccessTitle')}</DialogTitle>
          <DialogContentWrapper center="true">
            <Typography>{t('ReviewSuccessText')}</Typography>
          </DialogContentWrapper>
          <DialogActions sx={{ justifyContent: 'center', paddingBottom: 2 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => setSuccess(false)}
              aria-label={t('Close')}>
              {t('Close')}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};

export default AddReviewAction;
